import type { ItemVariantAxisOptions } from '../api/masters';
import type { UnitCondition } from '../generated/enums';

import { normalizeReference } from './referenceNumber';

/**
 * One line of a delivery: some quantity of one design in one configuration.
 *
 * <p>
 * The axis values are a map rather than a list because the set of axes is <em>data</em> — it comes
 * from the design's category and changes the moment the line names a different design. A list would
 * have to be rebuilt in step with that, and the stale entries it kept are the ones that get posted.
 * </p>
 */
export interface IntakeLine {
  /** Stable across re-orders and removals, unlike an index. Never sent to the server. */
  key: string;
  itemId: number | null;
  /** attributeId → valueId, exactly the axes the chosen design declares. */
  axisValues: Record<number, number>;
  quantity: number;
  condition: UnitCondition;
  purchaseCost: number | null;
  /**
   * A flat per-day hire price for this configuration, published as a rate card by the same call.
   *
   * Null leaves pricing alone, which is what a shop with a duration ladder already wants — it does
   * not withdraw a rate set earlier, because a delivery is not a statement about prices it says
   * nothing about.
   *
   * This is what stops the screen being a dead end: without a rate somewhere above them, the
   * garments booked in here cannot be booked *out* at all, and the shop has to leave for
   * `/rate-cards` before it can take a single wedding.
   */
  rentPerDay: number | null;
  /** Rent for the whole event, one price whatever the length (2026-10-17); wins over `rentPerDay`. */
  rentForEvent: number | null;
  /** What each day past the first adds to `rentForEvent`; null charges the event price flat. */
  extraDayRent: number | null;
  /**
   * Free text — one per line or comma separated — split only on submit.
   *
   * Kept as typed rather than as an array so a half-typed tag is not silently dropped mid-keystroke,
   * which is what an array parsed on every change would do.
   */
  serialCodes: string;
  /** Off means the server generates the tags from the variant's code. */
  ownSerials: boolean;
  /**
   * The purchase order line this fulfils, when receiving against an order — the design and its
   * configuration are then the order's and locked on screen. Null for stock outside any order.
   */
  purchaseOrderLineId: number | null;
  /** This line's own note, over the delivery's — a spreadsheet row's remark. Absent on screen lines. */
  notes?: string | null;
  /**
   * The shop's own number for each garment, in order (core `units/referenceNumber.ts`) — null or
   * blank where one has none. Absent, or all blank, sends none.
   */
  referenceNumbers?: (string | null)[];
}

/**
 * A whole delivery.
 *
 * <p>
 * <strong>The date and the note sit on the delivery, not on the line, and that is a claim about
 * the trade rather than a saving.</strong> A shop takes in one consignment on one day against one
 * invoice; asking for the purchase date eleven times invites eleven answers, and the reason a
 * storeman would want them to differ — two deliveries — is two deliveries.
 * </p>
 */
export interface IntakeDraft {
  /** ISO date. Applied to every line, because a delivery arrives on one day. */
  purchasedOn: string;
  /** The vendor it came from, marked as a supplier; null records none. Every line, like the date. */
  supplierVendorId: number | null;
  /** The supplier's own challan or invoice number, for the goods receipt. */
  supplierReference: string;
  notes: string;
  lines: IntakeLine[];
}

let sequence = 0;

export function emptyLine(): IntakeLine {
  sequence += 1;

  return {
    key: `line-${sequence}`,
    itemId: null,
    axisValues: {},
    quantity: 1,
    condition: 'New',
    purchaseCost: null,
    rentPerDay: null,
    rentForEvent: null,
    extraDayRent: null,
    serialCodes: '',
    ownSerials: false,
    purchaseOrderLineId: null,
  };
}

/** A delivery starts with one empty line, so the screen is never an empty table with a button. */
export function emptyDraft(today: string): IntakeDraft {
  return {
    purchasedOn: today,
    supplierVendorId: null,
    supplierReference: '',
    notes: '',
    lines: [emptyLine()],
  };
}

export function addLine(draft: IntakeDraft): IntakeDraft {
  return { ...draft, lines: [...draft.lines, emptyLine()] };
}

/**
 * Removing the last line leaves an empty one rather than none.
 *
 * A table with no rows and an "Add line" button is a screen that looks broken; there is no state in
 * which a delivery has nothing to say.
 */
export function removeLine(draft: IntakeDraft, key: string): IntakeDraft {
  const lines = draft.lines.filter((line) => line.key !== key);

  return { ...draft, lines: lines.length > 0 ? lines : [emptyLine()] };
}

export function updateLine(
  draft: IntakeDraft,
  key: string,
  changes: Partial<Omit<IntakeLine, 'key'>>,
): IntakeDraft {
  return {
    ...draft,
    lines: draft.lines.map((line) => (line.key === key ? { ...line, ...changes } : line)),
  };
}

/**
 * Naming a different design clears the axis values with it.
 *
 * Cleared rather than remapped: two designs sharing an attribute id is not a reason to assume the
 * same value was meant, and a carried-over colour is the kind of wrong that nobody re-reads.
 */
export function setLineItem(draft: IntakeDraft, key: string, itemId: number | null): IntakeDraft {
  return updateLine(draft, key, { itemId, axisValues: {} });
}

export function setLineAxis(
  draft: IntakeDraft,
  key: string,
  attributeId: number,
  valueId: number,
): IntakeDraft {
  const line = draft.lines.find((candidate) => candidate.key === key);

  if (!line) {
    return draft;
  }

  return updateLine(draft, key, { axisValues: { ...line.axisValues, [attributeId]: valueId } });
}

/** Splits typed tags on whitespace or commas, discarding the empties either produces. */
export function parseSerials(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((code) => code.trim())
    .filter(Boolean);
}

/** Garments and money across the whole delivery — what the summary panel states. */
export function draftTotals(draft: IntakeDraft): { units: number; cost: number } {
  return draft.lines.reduce(
    (total, line) => ({
      units: total.units + (line.itemId === null ? 0 : line.quantity),

      // Per garment, not per line — the same rule the server applies, so the two agree before
      // anything is posted.
      cost: total.cost + (line.itemId === null ? 0 : (line.purchaseCost ?? 0) * line.quantity),
    }),
    { units: 0, cost: 0 },
  );
}

/**
 * Why one line cannot be booked in yet, in the order a storeman would notice them.
 *
 * <p>
 * Returns sentences rather than a boolean because the screen shows them: "line 3 is not ready" with
 * no reason is worse than no message, on a page that can hold fifty lines.
 * </p>
 */
export function lineProblems(
  line: IntakeLine,
  axes: ItemVariantAxisOptions[] | undefined,
): string[] {
  const problems: string[] = [];

  if (line.itemId === null) {
    return ['Pick an item.'];
  }

  if (axes === undefined) {
    // Not a problem yet — the design's axes have not arrived. Reported as unready rather than as
    // complete, or a line could be submitted before anyone saw what it had to be told.
    return ['Loading this item’s options…'];
  }

  // A design whose category declares no variant-forming attribute cannot take stock at all —
  // `ItemVariantService.ResolveAsync` refuses it outright, because a garment with no axes has no
  // configuration to be told apart by. Refused here rather than only there so a delivery is not
  // rolled back whole for a line whose defect was visible before anything was posted.
  if (axes.length === 0) {
    return ['This item’s category has no variant-forming attributes yet, so it cannot take stock.'];
  }

  const missing = axes.filter((axis) => line.axisValues[axis.attributeId] === undefined);

  if (missing.length > 0) {
    problems.push(
      `Choose ${missing.map((axis) => axis.attributeName.toLowerCase()).join(' and ')}.`,
    );
  }

  if (!Number.isInteger(line.quantity) || line.quantity < 1 || line.quantity > 100) {
    problems.push('Quantity must be between 1 and 100.');
  }

  if (line.purchaseCost !== null && line.purchaseCost < 0) {
    problems.push('A purchase cost cannot be negative.');
  }

  if (line.rentPerDay !== null && line.rentPerDay < 0) {
    problems.push('A per-day price cannot be negative.');
  }

  if ((line.rentForEvent ?? 0) < 0 || (line.extraDayRent ?? 0) < 0) {
    problems.push('A rent cannot be negative.');
  }

  if (line.ownSerials) {
    const serials = parseSerials(line.serialCodes);

    // All or nothing, matching the server: a batch half-generated and half-transcribed is one the
    // storeman cannot tell apart on the rail afterwards.
    if (serials.length !== line.quantity) {
      problems.push(
        `Give exactly ${line.quantity} serial${line.quantity === 1 ? '' : 's'}, or turn tags off.`,
      );
    }

    if (new Set(serials.map((code) => code.toUpperCase())).size !== serials.length) {
      problems.push('The same serial appears twice on this line.');
    }
  }

  return problems;
}

/**
 * What stops the whole delivery being booked in.
 *
 * <p>
 * Cross-line duplicate tags are checked here rather than per line, because that is the only level
 * at which they are visible. The server refuses them too — line two's check sees line one's
 * garments — but only after the transaction has started, and a refusal a screen could have given
 * first is a round trip the storeman waits through.
 * </p>
 */
export function draftProblems(
  draft: IntakeDraft,
  axesByItem: Record<number, ItemVariantAxisOptions[]>,
): string[] {
  const problems: string[] = [];

  draft.lines.forEach((line, index) => {
    const forLine = lineProblems(line, line.itemId === null ? undefined : axesByItem[line.itemId]);

    forLine.forEach((problem) => problems.push(`Line ${index + 1}: ${problem}`));
  });

  const tags = draft.lines
    .filter((line) => line.ownSerials)
    .flatMap((line) => parseSerials(line.serialCodes))
    .map((code) => code.toUpperCase());

  const repeated = [...new Set(tags.filter((code, index) => tags.indexOf(code) !== index))];

  repeated.forEach((code) => problems.push(`Serial ${code} is on more than one line.`));

  return problems;
}

/** A line's reference numbers as sent: cleaned, one per garment, or null when none is given. */
function referencesFor(line: IntakeLine): (string | null)[] | null {
  const numbers = (line.referenceNumbers ?? []).map(normalizeReference);

  return numbers.some((number) => number !== null)
    ? Array.from({ length: line.quantity }, (_, index) => numbers[index] ?? null)
    : null;
}

/** One line of the request `POST /api/item-unit/batch` takes. */
export interface IntakeRequestLine {
  itemId: number;
  axisValues: { attributeId: number; valueId: number }[];
  quantity: number;
  condition: UnitCondition;
  purchaseCost: number | null;
  rentPerDay: number | null;
  rentForEvent: number | null;
  extraDayRent: number | null;
  purchasedOn: string | null;
  supplierVendorId: number | null;
  notes: string | null;
  serialCodes: string[] | null;
  purchaseOrderLineId: number | null;
  /** One per garment, cleaned (null where none), or null when the line carries none at all. */
  referenceNumbers: (string | null)[] | null;
}

/**
 * The delivery as the API wants it.
 *
 * <p>
 * The date and the note are copied onto every line, which is where the split between this shape and
 * the screen's shape is paid for. `AddItemUnitsRequest` carries them per line because it predates
 * the multi-line screen and a line remains meaningful on its own; the screen asks once because a
 * delivery has one date. Copying is the honest reconciliation — the alternative, moving them onto
 * the batch request, would leave the single-line endpoint unable to record either.
 * </p>
 */
export function toBatchRequest(draft: IntakeDraft): {
  lines: IntakeRequestLine[];
  supplierReference: string | null;
} {
  return {
    // Once, on the delivery's goods receipt rather than per line: it is the paper the delivery came with.
    supplierReference: draft.supplierReference.trim() || null,
    lines: draft.lines
      .filter((line): line is IntakeLine & { itemId: number } => line.itemId !== null)
      .map((line) => {
        const serials = parseSerials(line.serialCodes);

        return {
          itemId: line.itemId,
          axisValues: Object.entries(line.axisValues).map(([attributeId, valueId]) => ({
            attributeId: Number(attributeId),
            valueId,
          })),
          quantity: line.quantity,
          condition: line.condition,
          purchaseCost: line.purchaseCost,
          rentPerDay: line.rentPerDay,
          rentForEvent: line.rentForEvent,
          extraDayRent: line.extraDayRent,
          purchasedOn: draft.purchasedOn || null,
          supplierVendorId: draft.supplierVendorId,
          notes: line.notes?.trim() || draft.notes.trim() || null,
          serialCodes: line.ownSerials && serials.length > 0 ? serials : null,
          purchaseOrderLineId: line.purchaseOrderLineId,
          referenceNumbers: referencesFor(line),
        };
      }),
  };
}
